<template>
  <article class="subject-detail">
    <header class="subject-detail__header">
      <span>{{ data.trackLabel || (isEnglish ? 'DATA ANALYTICS TRACK' : '数据分析赛道 · 完整赛题') }}</span>
      <h2>{{ data.title }}</h2>
    </header>
    <div class="subject-detail__body">
      <template v-for="(block, index) in data.blocks">
        <h3 v-if="block.type === 'heading' || isSectionHeading(block.text)" :key="`heading-${index}`">{{ block.text }}</h3>
        <div
          v-else-if="block.type === 'paragraph' && !(index === 0 && data.skipFirstParagraph !== false)"
          :key="`p-${index}`"
          class="subject-detail__paragraph"
        >
          <p v-html="formatParagraph(block.text)"></p>
          <div
            v-if="isEvaluationParagraph(block.text)"
            class="subject-detail__downloads subject-detail__downloads--inline"
          >
            <div class="subject-detail__downloads-title">{{ isEnglish ? 'Evaluation metric scripts' : '评价指标计算附件' }}</div>
            <a href="/downloads/evaluate.py" download target="_blank" rel="noopener">{{ isEnglish ? 'Python evaluation script (evaluate.py)' : 'Python 评价指标计算方式（evaluate.py）' }}</a>
            <a href="/downloads/evaluate.R" download target="_blank" rel="noopener">{{ isEnglish ? 'R evaluation script (evaluate.R)' : 'R 评价指标计算方式（evaluate.R）' }}</a>
          </div>
        </div>
        <div v-else-if="block.type === 'table'" :key="`table-${index}`" class="subject-detail__table-wrap">
          <table class="subject-detail__table">
            <tbody>
              <tr v-for="(row, rowIndex) in block.rows" :key="rowIndex">
                <template v-if="rowIndex === 0">
                  <th v-for="(cell, cellIndex) in row" :key="cellIndex">{{ formatCell(cell) }}</th>
                </template>
                <template v-else>
                  <td v-for="(cell, cellIndex) in row" :key="cellIndex">{{ formatCell(cell) }}</td>
                </template>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </div>
    <div v-if="data.downloads && data.downloads.length" class="subject-detail__downloads">
      <div class="subject-detail__downloads-title">Downloads</div>
      <a v-for="download in data.downloads" :key="download.href" :href="download.href" download target="_blank" rel="noopener">{{ download.label }}</a>
    </div>
  </article>
</template>

<script>
export default {
  name: 'SubjectDetailModal',
  props: { data: { type: Object, required: true } },
  computed: {
    isEnglish () { return this.$store.state.isEnLang }
  },
  methods: {
  normalizeText (value) {
    return String(value || '')
      .replace(/详细排版要求详见报名课堂派报名说明/g, '详细排版要求详见课堂派报名说明')
      .replace(
        /采用多维度加权打分方式综合评估选手预测能力(?:。|，提供python和R两个版本的评价指标计算方式)?/,
        '采用多维度加权打分方式综合评估选手预测能力，提供python和R两个版本的评价指标计算方式'
      )
  },
  isEvaluationParagraph (text) {
    const value = String(text || '')
    return value.includes('采用多维度加权打分方式综合评估选手预测能力') || value.includes('Python and R implementations of the evaluation metrics')
  },
    isSectionHeading (text) {
      const value = String(text || '')
      // Numbered paragraphs such as “（1）业务背景：……” remain regular text;
      // only short standalone labels and subsection numbers become headings.
      return /^\d+\.\d+/.test(value) || /^（\d+）[^：:\n]{1,20}$/.test(value) || /^\(\d+\)\s+[^:：\n]{1,60}$/.test(value)
    },
    formatCell (value) {
      return String(value || '').replace(/\s\/\s/g, '\n')
    },
    formatParagraph (value) {
      const escaped = this.normalizeText(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
      const withLabels = escaped.replace(/^((?:(?:（\d+）|\(\d+\))\s*)?(?:业务背景|研究目标|数据说明|产出要求|评价标准|注意事项|Business background|Research objective|Data description|Deliverables|Evaluation criteria|Notes)[：:])/, '<strong class="subject-detail__label">$1</strong>')
      return withLabels
        .replace(/(Attachment\s*2\s*:\s*International Track\s*[—-]\s*Topics\s*&amp;\s*Rules)/gi, '<strong class="subject-detail__attachment-ref">$1</strong>')
        .replace(/(Attachment\s*3\s*:\s*Registration Form(?:\s*\([^)]*\))?)/gi, '<strong class="subject-detail__attachment-ref">$1</strong>')
    }
  }
}
</script>

<style lang="scss" scoped>
.subject-detail { color:#273247; background:#fff; text-align:left; }
.subject-detail__header { padding:28px 34px 20px; border-bottom:1px solid #e5e9ef; background:#fff; }
.subject-detail__header > span { color:#315fd8; font-size:12px; font-weight:700; letter-spacing:.04em; }
.subject-detail__header h2 { margin-top:10px; color:#182640; font-size:24px; line-height:1.4; font-weight:700; }
.subject-detail__body { padding:24px 34px 34px; text-align:left; }
.subject-detail__body p { margin:0 0 16px; color:#5d687a; font-size:14px; line-height:1.9; }
.subject-detail__body p :deep(.subject-detail__label) { color:#273247; font-weight:700; }
.subject-detail__body p :deep(.subject-detail__attachment-ref) { display:inline-block; padding:1px 6px; border-radius:4px; color:#315fd8; background:#edf3ff; font-weight:700; }
.subject-detail__body h3 { margin:25px 0 12px; padding:0 0 7px; border-bottom:1px solid #e5e9ef; color:#1f2d47; font-size:16px; line-height:1.5; }
.subject-detail__table-wrap { margin:18px 0 24px; overflow-x:auto; border:1px solid #dfe5ed; }
.subject-detail__table { width:100%; min-width:560px; border-collapse:collapse; color:#5d687a; font-size:12px; line-height:1.65; }
.subject-detail__table th { color:#243553; font-weight:700; background:#f5f7fa; }
.subject-detail__table th,.subject-detail__table td { padding:10px 12px; border-bottom:1px solid #e8edf4; border-right:1px solid #e8edf4; vertical-align:top; text-align:left; white-space:pre-line; }
.subject-detail__table tr:last-child td { border-bottom:0; }
.subject-detail__table th:last-child,.subject-detail__table td:last-child { border-right:0; }
.subject-detail__downloads { margin:0 34px 30px; padding:18px 20px; border-top:1px solid #e5e9ef; background:#f7f9fc; }
.subject-detail__downloads-title { margin-bottom:10px; color:#1f2d47; font-size:13px; font-weight:700; }
.subject-detail__downloads a { display:block; margin-top:8px; color:#315fd8; font-size:13px; line-height:1.5; text-decoration:underline; }
.subject-detail__downloads--inline { margin:2px 0 24px; }
@include mobile {
  .subject-detail__header { padding:22px 16px 16px; }
  .subject-detail__header > span { display:block; padding-right:28px; font-size:10px; line-height:1.5; }
  .subject-detail__header h2 { padding-right:20px; font-size:19px; line-height:1.35; overflow-wrap:anywhere; }
  .subject-detail__body { padding:18px 16px 24px; }
  .subject-detail__body p { font-size:13px; line-height:1.75; }
  .subject-detail__body h3 { font-size:15px; }
  .subject-detail__table { min-width:500px; font-size:11px; }
  .subject-detail__table th,.subject-detail__table td { padding:9px 10px; }
  .subject-detail__downloads { margin:0 16px 20px; padding:15px; }
  .subject-detail__downloads--inline { margin:2px 0 22px; }
}
</style>
